import { useState, useEffect } from 'react'
import type { FC } from 'react'
import { Provider as ReduxProvider, useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { useNavigate } from 'react-router'
import qs from 'query-string'

import { InterfaceContent } from '~/shared/ui'
import WorklistSidebar from './WorklistSidebar'
import { worklistContext, worklistStore } from './store'
import { isFilterPreference, WorklistFilterPreference } from './preferencesSlice'
import { isFiniteNumber, isRecord } from '~/shared/lib/guards'
import { logger } from '~/shared/lib/logger'
import WorkUnitTable from './WorkUnitTable'

export interface WorklistFilterUrlParam {
  worklistId: number
  filters: WorklistFilterPreference
}

/**
 * Deep-link filters arrive as JSON in a URL the operator can edit or paste, so the shape is checked
 * rather than asserted: a malformed param leaves the worklist on its defaults instead of sending a
 * broken `filters` query param to the worklist endpoint.
 *
 * @knipskip - used in-file and asserted directly by `Worklist.vitest.tsx`; nothing outside the
 * feature imports it.
 */
export const parseWorklistFilterParam = (raw: string): WorklistFilterUrlParam | null => {
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch (_) {
    return null
  }
  if (!isRecord(parsed) || !isFiniteNumber(parsed.worklistId)) return null
  if (!isFilterPreference(parsed.filters)) return null
  return { worklistId: parsed.worklistId, filters: parsed.filters }
}
const Worklist: FC = () => {
  const [worklistId, setWorklistId] = useState<number>()
  const [worklistFiltersUrlParam, setWorklistFiltersUrlParam] = useState<WorklistFilterUrlParam>()
  const location = useLocation()
  const path = location.pathname
  const urlParams = qs.parse(location.search)
  const providers = useSelector(state => state.providers)
  const assigneeGroups = useSelector(state => state.assigneeGroups)
  const navigate = useNavigate()

  // The worklist and its filters follow the URL whenever those params change. They are compared
  // as strings, because a repeated param parses to a new array on every render.
  const worklistParam = urlParams.worklistId == null ? undefined : String(urlParams.worklistId)
  const filtersParam =
    typeof urlParams.worklistFilters === 'string' ? urlParams.worklistFilters : undefined
  const [readWorklistParam, setReadWorklistParam] = useState<string>()
  if (worklistParam !== readWorklistParam) {
    setReadWorklistParam(worklistParam)
    if (worklistParam) setWorklistId(parseInt(worklistParam))
  }
  const [readFiltersParam, setReadFiltersParam] = useState<string>()
  if (filtersParam !== readFiltersParam) {
    setReadFiltersParam(filtersParam)
    const worklistFilters = filtersParam ? parseWorklistFilterParam(filtersParam) : undefined
    if (worklistFilters) setWorklistFiltersUrlParam(worklistFilters)
    if (worklistFilters === null)
      logger.warn('[Worklist] Ignoring a malformed worklistFilters URL param')
  }

  useEffect(() => {
    const params = {
      ...(worklistId && { worklistId: worklistId }),
      ...(worklistFiltersUrlParam?.filters && {
        worklistFilters: JSON.stringify(worklistFiltersUrlParam),
      }),
    }
    void navigate(`${path}?${qs.stringify(params)}`, { replace: true })
  }, [worklistId, worklistFiltersUrlParam, navigate, path])

  return (
    <ReduxProvider context={worklistContext} store={worklistStore}>
      <InterfaceContent
        sidebar={<WorklistSidebar worklistId={worklistId} setWorklistId={setWorklistId} />}
      >
        <WorkUnitTable
          worklistId={worklistId}
          providers={providers}
          assigneeGroups={assigneeGroups}
          setWorklistFiltersUrlParam={setWorklistFiltersUrlParam}
          worklistFiltersUrlParam={worklistFiltersUrlParam}
        />
      </InterfaceContent>
    </ReduxProvider>
  )
}

export default Worklist
